import { useQuery } from "@tanstack/react-query";
import { LuTrendingUp, LuMail, LuMessageSquare } from "react-icons/lu";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonTable } from "@/components/ui/skeleton";

export function TopConsumersPage() {
  const q = useQuery({
    queryKey: ["console", "admin", "top-consumers"],
    queryFn: () => api.adminTopConsumers(),
  });
  return (
    <>
      <PageHeader
        icon={LuTrendingUp}
        title="Top consumers"
        description="Heaviest users this month by inbox count and message volume."
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {q.isLoading && <><SkeletonTable rows={6} /><SkeletonTable rows={6} /></>}
        {q.data && (
          <>
            <ListCard
              title="Most inboxes"
              icon={LuMail}
              rows={q.data.byInbox.map((u) => ({
                key: u.id,
                primary: u.email,
                secondary: u.subscription_tier,
                metric: u.inbox_count.toLocaleString(),
                metricLabel: "inboxes",
              }))}
            />
            <ListCard
              title="Most messages received"
              icon={LuMessageSquare}
              rows={q.data.byMessage.map((u) => ({
                key: u.id,
                primary: u.email,
                secondary: u.subscription_tier,
                metric: u.message_count.toLocaleString(),
                metricLabel: "msgs",
              }))}
            />
          </>
        )}
      </div>
    </>
  );
}

function ListCard({ title, icon: Icon, rows }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  rows: Array<{ key: string; primary: string; secondary: string; metric: string; metricLabel: string }>;
}) {
  return (
    <Card>
      <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
        <Icon className="w-4 h-4 text-[var(--color-text-subtle)]" />
        <p className="text-sm font-semibold text-[var(--color-text)]">{title}</p>
      </div>
      <div className="divide-y divide-[var(--color-border)]">
        {rows.length === 0 ? (
          <p className="px-5 py-4 text-xs text-[var(--color-text-subtle)]">No data this month.</p>
        ) : (
          rows.map((r, i) => (
            <div key={r.key} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[10px] tabular-nums text-[var(--color-text-subtle)] w-5 text-right">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-[var(--color-text)] truncate">{r.primary}</p>
                  <p className="text-[11px] uppercase tracking-wider text-[var(--color-text-subtle)]">
                    {r.secondary}
                  </p>
                </div>
              </div>
              <span className="text-sm font-semibold text-[var(--color-text)] tabular-nums shrink-0">
                {r.metric}{" "}
                <span className="text-[10px] text-[var(--color-text-subtle)]">{r.metricLabel}</span>
              </span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
